'use client';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

/** Chaves iguais à API (`POST obras/:id/reacao` com `reacao_tipo`) e ao app móvel. */
const API_REACTION_IDS = ['amei', 'gostei', 'uau', 'fogo', 'top', 'triste', 'raiva', 'ruim'] as const;
type ApiReactionId = (typeof API_REACTION_IDS)[number];

interface ReactionCounts {
    [key: string]: number;
}

function sumReactionCounts(counts: ReactionCounts): number {
    return API_REACTION_IDS.reduce((s, k) => s + (Number(counts[k]) || 0), 0);
}

function parseReacoesFromApiPayload(data: Record<string, unknown> | null | undefined): {
    counts: ReactionCounts;
    userReaction: string | null;
    total: number;
} {
    if (!data || typeof data !== 'object') {
        return { counts: {}, userReaction: null, total: 0 };
    }
    const raw = data.reacoes;
    const o: Record<string, unknown> =
        raw && typeof raw === 'object'
            ? (raw as Record<string, unknown>)
            : API_REACTION_IDS.some((k) => data[k] != null)
              ? data
              : {};
    const counts: ReactionCounts = {};
    for (const k of API_REACTION_IDS) {
        counts[k] = Number(o[k] ?? 0) || 0;
    }
    const minha = o.minha_reacao ?? data.minha_reacao;
    const userReaction =
        typeof minha === 'string' && (API_REACTION_IDS as readonly string[]).includes(minha) ? minha : null;
    let total =
        Number(o.total ?? data.total_reacoes ?? data.reactions_total ?? 0) || 0;
    if (!total) total = sumReactionCounts(counts);
    return { counts, userReaction, total };
}

async function fetchReactions(entityType: 'obra' | 'capitulo', entityId: number) {
    const endpoint = entityType === 'obra' ? `obras/${entityId}` : `capitulos/${entityId}`;
    const data = unwrapApiResponse<Record<string, unknown>>(await api.get(endpoint));
    return parseReacoesFromApiPayload(data);
}

function reactToEntity(entityType: 'obra' | 'capitulo', entityId: number, tipo: string) {
    const endpoint = entityType === 'obra' ? `obras/${entityId}/reacao` : `capitulos/${entityId}/reacao`;
    return api.post(endpoint, { reacao_tipo: tipo }, { requiresAuth: true });
}

interface ReactionPickerProps {
    entityId: number;
    entityType: 'obra' | 'capitulo';
    onReactionChange?: () => void;
    variant?: 'default' | 'compact';
}

const REACTION_TYPES: { id: ApiReactionId; emoji: string; label: string }[] = [
    { id: 'amei', emoji: '❤️', label: 'Amei' },
    { id: 'gostei', emoji: '👍', label: 'Gostei' },
    { id: 'uau', emoji: '😮', label: 'Uau' },
    { id: 'fogo', emoji: '🔥', label: 'Fogo' },
    { id: 'top', emoji: '⭐', label: 'Top' },
    { id: 'triste', emoji: '😢', label: 'Triste' },
    { id: 'raiva', emoji: '😡', label: 'Raiva' },
    { id: 'ruim', emoji: '👎', label: 'Ruim' },
];

export default function ReactionPicker({ entityId, entityType, onReactionChange, variant = 'default' }: ReactionPickerProps) {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [counts, setCounts] = useState<ReactionCounts>({});
    const [userReaction, setUserReaction] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const isCompact = variant === 'compact';

    const loadReactions = useCallback(async () => {
        if (!entityId) return;
        try {
            const data = await fetchReactions(entityType, entityId);
            setCounts(data.counts || {});
            setUserReaction(data.userReaction);
            setTotal(data.total || 0);
        } catch (error) {
            console.error('Erro ao carregar reações:', error);
        } finally {
            setIsLoading(false);
        }
    }, [entityId, entityType]);

    useEffect(() => {
        setIsLoading(true);
        void loadReactions();
    }, [loadReactions, user?.usr_id]);

    const handleReact = async (type: ApiReactionId) => {
        if (!isAuthenticated) {
            navigate(`/entrar?next=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')}`);
            return;
        }

        const isRemoving = userReaction === type;
        const prevReaction = userReaction;

        const next: ReactionCounts = { ...counts };
        if (isRemoving) {
            next[type] = Math.max(0, (Number(next[type]) || 0) - 1);
        } else {
            if (prevReaction && prevReaction !== type) {
                next[prevReaction] = Math.max(0, (Number(next[prevReaction]) || 0) - 1);
            }
            next[type] = (Number(next[type]) || 0) + 1;
        }
        setUserReaction(isRemoving ? null : type);
        setCounts(next);
        setTotal(sumReactionCounts(next));

        try {
            await reactToEntity(entityType, entityId, type);
            onReactionChange?.();
        } catch (error) {
            console.error('Erro ao reagir:', error);
            void loadReactions();
        }
    };

    if (isLoading) return <div className={`w-full animate-pulse bg-white/5 rounded-xl ${isCompact ? 'h-16' : 'h-32'}`} />;

    return (
        <div
            className={`flex flex-col items-center transition-all duration-300 ${
                isCompact
                    ? 'gap-3 p-4 bg-white/[0.03] border border-white/10 rounded-2xl'
                    : 'gap-6 p-8 bg-white/[0.03] border border-white/10 rounded-3xl'
            }`}
        >
            <div className="flex w-full max-w-4xl flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className={`font-black uppercase tracking-[0.2em] text-white/50 ${isCompact ? 'text-xs' : 'text-base'}`}>
                    Reações
                </h3>
                <span className={`text-white/40 ${isCompact ? 'text-[10px]' : 'text-xs'}`}>
                    {total} {total === 1 ? 'reação' : 'reações'}
                </span>
            </div>

            <div
                className={`grid w-full grid-cols-4 items-start justify-items-center md:grid-cols-8 ${
                    isCompact ? 'max-w-2xl gap-3 md:gap-4' : 'max-w-4xl gap-4 md:gap-8'
                }`}
            >
                {REACTION_TYPES.map((reaction) => {
                    const isActive = userReaction === reaction.id;
                    const count = counts[reaction.id] || 0;

                    return (
                        <button
                            key={reaction.id}
                            type="button"
                            onClick={() => void handleReact(reaction.id)}
                            className={`group flex flex-col items-center transition-all duration-500 ${
                                isCompact ? 'gap-1' : 'gap-3'
                            } ${isActive ? 'scale-110 active:scale-95' : 'opacity-60 hover:opacity-100 hover:scale-110 active:scale-95'}`}
                        >
                            <div
                                className={`rounded-full transition-all duration-500 ${
                                    isCompact ? 'p-2' : 'p-5'
                                } ${
                                    isActive
                                        ? 'border-2 border-orange-500/50 bg-orange-500/20 shadow-[0_0_30px_rgba(249,115,22,0.25)]'
                                        : 'border border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                                }`}
                            >
                                <span
                                    className={`block transition-transform duration-500 ${
                                        isCompact ? 'text-xl' : 'text-4xl'
                                    } ${isActive ? 'rotate-12 scale-110' : 'group-hover:-rotate-12'}`}
                                >
                                    {reaction.emoji}
                                </span>
                            </div>

                            <div className="flex flex-col items-center gap-0.5">
                                <span
                                    className={`font-black uppercase tracking-[0.15em] transition-colors ${
                                        isCompact ? 'text-[8px]' : 'text-[10px]'
                                    } ${isActive ? 'text-orange-400' : 'text-white/30 group-hover:text-white/60'}`}
                                >
                                    {reaction.label}
                                </span>

                                <span
                                    className={`font-bold tabular-nums transition-colors ${
                                        isCompact ? 'text-[9px]' : 'text-[11px]'
                                    } ${isActive ? 'text-orange-500' : 'text-white/20 group-hover:text-white/40'}`}
                                >
                                    {count}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
